// /home/labdoo/zitech-nexus/frontend/src/pages/admin/ContactMessagesList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactAPI } from '../../services/api';
import './ContactMessagesList.css';

// SVG Icons
const Icons = {
  View: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  MarkRead: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  MarkUnread: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cml-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" />
      <path d="M22 6L12 13L2 6" />
    </svg>
  ),
  Email: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  User: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Message: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
};

interface ContactMessage {
  id: number;
  name: string;
  email: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

const ContactMessagesList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await contactAPI.getAllMessages();
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await contactAPI.markAsRead(id);
      fetchMessages();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const getStatusColor = (isRead: boolean) => {
    return isRead ? '#10b981' : '#ef4444';
  };

  const getStatusBackground = (isRead: boolean) => {
    return isRead ? '#d1fae5' : '#fee2e2';
  };

  const filteredMessages = filter === 'all' 
    ? messages 
    : filter === 'read' 
      ? messages.filter(m => m.is_read)
      : messages.filter(m => !m.is_read);

  const counts = {
    all: messages.length,
    read: messages.filter(m => m.is_read).length,
    unread: messages.filter(m => !m.is_read).length,
  };

  if (loading) {
    return (
      <div className="cml-loading-container">
        <div className="cml-spinner">
          <Icons.Spinner />
        </div>
        <p className="cml-loading-text">Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="cml-container">
      {/* Header */}
      <div className="cml-header">
        <div className="cml-header-left">
          <h1 className="cml-title">Contact Messages</h1>
          <p className="cml-subtitle">Manage and respond to contact form submissions</p>
        </div>
        <div className="cml-header-actions">
          <span className="cml-stats-badge">
            <Icons.Message />
            Unread: {counts.unread}
          </span>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="cml-stats-bar">
        <div className="cml-stat-item">
          <span className="cml-stat-label">Total Messages</span>
          <span className="cml-stat-value">{counts.all}</span>
        </div>
        <div className="cml-stat-divider"></div>
        <div className="cml-stat-item">
          <span className="cml-stat-label">Unread</span>
          <span className="cml-stat-value cml-stat-unread">{counts.unread}</span>
        </div>
        <div className="cml-stat-divider"></div>
        <div className="cml-stat-item">
          <span className="cml-stat-label">Read</span>
          <span className="cml-stat-value cml-stat-read">{counts.read}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="cml-filter-bar">
        <button 
          className={`cml-filter-btn ${filter === 'all' ? 'cml-filter-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
          <span className="cml-filter-count">{counts.all}</span>
        </button>
        <button 
          className={`cml-filter-btn ${filter === 'unread' ? 'cml-filter-active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Unread
          <span className="cml-filter-count">{counts.unread}</span>
        </button>
        <button 
          className={`cml-filter-btn ${filter === 'read' ? 'cml-filter-active' : ''}`}
          onClick={() => setFilter('read')}
        >
          Read
          <span className="cml-filter-count">{counts.read}</span>
        </button>
      </div>

      {/* Table */}
      <div className="cml-table-wrapper">
        <table className="cml-table">
          <thead>
            <tr>
              <th className="cml-col-id">ID</th>
              <th className="cml-col-name">Name</th>
              <th className="cml-col-email">Email</th>
              <th className="cml-col-message">Message</th>
              <th className="cml-col-status">Status</th>
              <th className="cml-col-date">Received</th>
              <th className="cml-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMessages.length === 0 ? (
              <tr>
                <td colSpan={7} className="cml-empty-state">
                  <Icons.Empty />
                  <p className="cml-empty-text">No messages found</p>
                  <p className="cml-empty-subtext">Messages from your contact form will appear here</p>
                </td>
              </tr>
            ) : (
              filteredMessages.map((message) => (
                <tr key={message.id} className={`cml-table-row ${!message.is_read ? 'cml-row-unread' : ''}`}>
                  <td className="cml-col-id">
                    <span className="cml-id-badge">#{message.id}</span>
                  </td>
                  <td className="cml-col-name">
                    <span className="cml-name">
                      <Icons.User />
                      {message.name}
                    </span>
                  </td>
                  <td className="cml-col-email">
                    <a href={`mailto:${message.email}`} className="cml-email-link">
                      <Icons.Email />
                      {message.email}
                    </a>
                  </td>
                  <td className="cml-col-message">
                    <span className="cml-message-preview" title={message.message}>
                      {message.message.length > 120 
                        ? message.message.substring(0, 120) + '...' 
                        : message.message}
                    </span>
                  </td>
                  <td className="cml-col-status">
                    <span 
                      className="cml-status-badge"
                      style={{
                        background: getStatusBackground(message.is_read),
                        color: getStatusColor(message.is_read)
                      }}
                    >
                      <span className="cml-status-dot" style={{ background: getStatusColor(message.is_read) }}></span>
                      {message.is_read ? 'Read' : 'Unread'}
                    </span>
                  </td>
                  <td className="cml-col-date">
                    <span className="cml-date-text">
                      <Icons.Calendar />
                      {new Date(message.created_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </td>
                  <td className="cml-col-actions">
                    <div className="cml-actions">
                      <button 
                        className="cml-action-btn cml-action-view"
                        onClick={() => navigate(`/dashboard/admin/contact/${message.id}`)}
                        title="View Message"
                      >
                        <Icons.View />
                      </button>
                      {!message.is_read && (
                        <button 
                          className="cml-action-btn cml-action-mark-read"
                          onClick={() => markAsRead(message.id)}
                          title="Mark as Read"
                        >
                          <Icons.MarkRead />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContactMessagesList;